import { useCallback, useEffect, useMemo, useState } from 'react';

import { clientConfig } from '@/config/clientConfig';
import { getAssetValueMultiplier, useAssets } from '@/entities/assets';
import { TBeneficiariesSizes } from '@/entities/beneficiary/hooks/useBeneficiary/types';
import { useScenariosList } from '@/entities/scenarios';
import { AccountStatus, ApiScenarioResponse, ScenarioStatus, useApi } from '@/shared/api';
import { useCurrencyRates } from '@/shared/hooks/currencies/useCurrencyRates';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { BeneficiaryDto, BeneficiaryGroup } from '@/shared/lib/sj-orm/models/beneficiary.dto';
import { DelegatingRequestStatus } from '@/shared/lib/sj-orm/models/delegating-request.dto';
import { useProfileStore } from '@/shared/store/profile.store';

import { useApiBeneficiaries } from '../useApiBeneficiaries';

type BeneficiaryValueInfo = BeneficiaryDto & {
  value: { currency: string; amount: number };
  isActivated: boolean;
  scenarios: ApiScenarioResponse[];
};

export const useBeneficiary = () => {
  const { loading: scenariosLoading, scenarios } = useScenariosList({ lazy: true });
  const { beneficiaries } = useApiBeneficiaries();

  const { baseCurrency } = useProfileStore();
  const { getAsset } = useAssets();
  const sjDb = useSJDatabase();
  const api = useApi();
  const { sumPricesList } = useCurrencyRates();
  const [sizes, setSizes] = useState<TBeneficiariesSizes>({
    all: 0,
    [BeneficiaryGroup.CIRCLE]: 0,
    [BeneficiaryGroup.FAMILY_CLAN]: 0,
    [BeneficiaryGroup.FAMILY]: 0,
  });

  useEffect(() => {
    const clanLength =
      sjDb.collections.beneficiaries?.findMany((beneficiary) => {
        return beneficiary.group === BeneficiaryGroup.FAMILY_CLAN;
      })?.length || 0;
    const circleLength =
      sjDb.collections.beneficiaries?.findMany(
        (beneficiary) => beneficiary.group === BeneficiaryGroup.CIRCLE,
      )?.length || 0;
    const familyLength =
      sjDb.collections.beneficiaries?.findMany(
        (beneficiary) => beneficiary.group === BeneficiaryGroup.FAMILY,
      )?.length || 0;

    setSizes({
      [BeneficiaryGroup.FAMILY_CLAN]: clanLength,
      [BeneficiaryGroup.FAMILY]: familyLength,
      [BeneficiaryGroup.CIRCLE]: circleLength,
      all: sjDb.collections.beneficiaries?.size() || 0,
    });
  }, [sjDb]);

  const getBeneficiariesList = useCallback(
    (
      group: BeneficiaryGroup | 'all',
      sort?: (first: BeneficiaryDto, second: BeneficiaryDto) => number,
      filters?: {
        countries: Array<string>;
        groups: Array<BeneficiaryGroup>;
      },
    ): BeneficiaryDto[] => {
      const result: BeneficiaryDto[] = [];
      const groups =
        group === 'all'
          ? filters?.groups?.length
            ? [...filters.groups]
            : [BeneficiaryGroup.FAMILY, BeneficiaryGroup.FAMILY_CLAN, BeneficiaryGroup.CIRCLE]
          : [group];
      for (const group_ of groups) {
        const items_ = sjDb.collections.beneficiaries?.findMany(
          (beneficiary) =>
            filters?.countries?.length
              ? filters.countries.includes(beneficiary.linkedCountryCode)
              : true,
          sort ? sort : undefined,
        );

        const items = items_?.filter((el) => el.group === group_);

        result.push(...(items || []));
      }
      return Array.from(new Set(result));
    },
    [sjDb],
  );

  const getBeneficiary = useCallback(
    (id: string | number) => {
      if (typeof id === 'string') {
        return sjDb.collections.beneficiaries?.findById(id);
      }

      return sjDb.collections.beneficiaries?.findOne(
        (beneficiary) => beneficiary.centralDbProfileId === id,
      );
    },
    [sjDb],
  );

  const getBeneficiaryAdditionalInfo = useCallback(
    (id: string) => {
      return sjDb.collections.beneficiaryPersonalDataDocuments?.findOne(
        (document) => document.beneficiaryDtoId === id,
      );
    },
    [sjDb],
  );

  const getBeneficiaryScenarios = useCallback(
    (beneficiary: BeneficiaryDto) => {
      const relatedScenarios = scenarios?.filter((scenario) =>
        scenario.transfers.some(
          (transfer) => transfer.recipientProfileId === beneficiary.centralDbProfileId,
        ),
      );

      return relatedScenarios ?? [];
    },
    [scenarios],
  );

  const getBeneficiaryNotifications = useCallback(
    (id: string) => {
      return sjDb.collections.delegatingRequests?.findMany(
        (request) =>
          request.status === DelegatingRequestStatus.WAITING_FOR_APPROVE &&
          request.relatedEntity?.dtoId === id,
      );
    },
    [sjDb],
  );

  const getBeneficiariesWithValue = useCallback(
    async (id?: string) => {
      if (!scenarios) return undefined;

      // const resBeneficiaries = await getApiBeneficiaries();
      // const beneficiariesList = resBeneficiaries.data;

      const result: Record<string, BeneficiaryValueInfo> = {};
      const beneficiariesAssets: Record<string, Array<string>> = {};

      for (const scenario of scenarios) {
        if (scenario.status !== ScenarioStatus.Done) continue;

        const beneficiary = sjDb.collections.beneficiaries?.findOne((b) =>
          scenario.transfers.some(
            (transfer) => transfer.recipientProfileId === b.centralDbProfileId,
          ),
        );

        if (!beneficiary) continue;

        const assets = sjDb.collections.assets?.findMany(
          (asset) => scenario.dtoIds?.includes(asset.id),
        );

        if (!assets || !assets.length) continue;

        const assetIds = beneficiariesAssets[beneficiary.id]
          ? beneficiariesAssets[beneficiary.id]
          : [];

        assets.forEach((asset) => {
          if (assetIds.includes(asset.id)) return;
          assetIds.push(asset.id);
        });

        beneficiariesAssets[beneficiary.id] = assetIds;

        const prices = assetIds.reduce<{ currency: string; amount: number }[]>((acc, cur) => {
          const assetData = getAsset(cur);
          if (!assetData) return acc;
          const multiplier = getAssetValueMultiplier(assetData);
          const value = assetData?.purchaseValue;

          if (value?.from && value?.value) {
            return acc.concat({ currency: value.from, amount: value.value * multiplier });
          } else {
            return acc;
          }
        }, []);

        const value = await sumPricesList(prices);

        result[beneficiary.id] = {
          ...beneficiary,
          value,
          isActivated:
            beneficiaries?.find((b) => b.id === beneficiary.centralDbProfileId)?.status ===
            AccountStatus.ACTIVE,
          scenarios: [],
        };
      }

      const resultIds = Object.keys(result);

      for (const b of getBeneficiariesList('all')) {
        if (resultIds.includes(b.id)) {
          result[b.id] = {
            ...result[b.id],
            scenarios: scenarios.filter((oneScenario) =>
              oneScenario.transfers.some(
                (transfer) => transfer.recipientProfileId === b.centralDbProfileId,
              ),
            ),
          };

          continue;
        }

        result[b.id] = {
          ...b,
          value: {
            currency: baseCurrency ?? clientConfig.defaultProfileBaseCurrency,
            amount: 0,
          },
          isActivated:
            beneficiaries?.find((_b) => _b.id === b.centralDbProfileId)?.status ===
            AccountStatus.ACTIVE,

          scenarios: scenarios.filter((oneScenario) =>
            oneScenario.transfers.some(
              (transfer) => transfer.recipientProfileId === b.centralDbProfileId,
            ),
          ),
        };
      }

      return id ? (result[id] ? [result[id]] : undefined) : Object.values(result);
    },
    [sjDb, scenarios, beneficiaries],
  );

  const getAllBeneficiaryPersonalIdentifiers = useCallback(() => {
    return sjDb.collections.beneficiaryPersonalDataDocuments?.findMany(() => true);
  }, [sjDb]);

  const isBeneficiaryWithScenario = useCallback(
    (beneficiaryCentralDbId: number) => {
      return scenarios?.some((scenario) =>
        scenario.transfers.some(
          (transfer) => transfer.recipientProfileId === beneficiaryCentralDbId,
        ),
      );
    },
    [scenarios],
  );

  const deleteBeneficiary = useCallback(
    (beneficiaryData: BeneficiaryDto) => {
      sjDb.collections.beneficiaries?.removeById(beneficiaryData.id);
      api.beneficiaryRemove(beneficiaryData.centralDbProfileId);
    },
    [sjDb],
  );

  return useMemo(
    () => ({
      sizes,
      getBeneficiariesList,
      getBeneficiary,
      getBeneficiaryAdditionalInfo,
      getBeneficiaryScenarios,
      getBeneficiaryNotifications,
      getBeneficiariesWithValue,
      getAllBeneficiaryPersonalIdentifiers,
      isBeneficiaryWithScenario,
      deleteBeneficiary,
      loading: scenariosLoading,
    }),
    [
      sizes,
      getBeneficiariesList,
      getBeneficiary,
      getBeneficiaryAdditionalInfo,
      getBeneficiaryScenarios,
      getBeneficiaryNotifications,
      getBeneficiariesWithValue,
      getAllBeneficiaryPersonalIdentifiers,
      isBeneficiaryWithScenario,
      deleteBeneficiary,
      scenariosLoading,
    ],
  );
};
